import { type NewDesignConfig, type ResponseErrorConfig, useOrgOrganizationsDesignRetrieve } from "@innoclouds/api";
import { experimental_createQueryPersister } from "@tanstack/query-persist-client-core";
import { keepPreviousData, type QueryObserverResult, type RefetchOptions } from "@tanstack/vue-query";
import { defu } from "defu";
import { defineStore } from "pinia";
import tinycolor from "tinycolor2";
import { computed, watch } from "vue";
import { isPreviewMode } from "../preview/mode";
import { syncDesignFonts } from "../utils/fonts";
import { useMainStore } from "./main";
import { useOrgUnitStore } from "./org-unit";

export const THEME_HASH_KEY = "themeHash";

export const applyCssVar = (variable: string, value: string | null | undefined, priority?: string) =>
	document.documentElement.style.setProperty(`--${variable}`, value || null, priority);

const persister = experimental_createQueryPersister({ storage: localStorage });

const previewPreloaderOf = (design: NewDesignConfig) =>
	[...(design.kiosk_preview_preloaders ?? [])].sort((a, b) => a.order - b.order).find((item) => item.media_type === "video" && item.url)?.url ?? null;

const applyColorVars = (data?: NewDesignConfig | null) => {
	if (!data) return;

	applyCssVar("primaryColor", data.primary_color);
	applyCssVar("accentColor", data.accent_color);
	applyCssVar("accentLightColor", data.accent_light_color);
	applyCssVar("accentDarkColor", data.accent_dark_color);
	applyCssVar("bgButtonColor", data.bg_button_color);
	applyCssVar("inactiveButtonColor", data.inactive_button_color);
	applyCssVar("cardsColor", data.cards_color);
	applyCssVar("textColor", data.text_color);
	applyCssVar("nonworkingBgColor", data.nonworking_bg_color);
	applyCssVar("mainPageFont", data.main_page_font_family);
	applyCssVar("kiosk-overlay-background", data.kiosk_auth_overlay?.background_color);
	applyCssVar("kiosk-overlay-border", data.kiosk_auth_overlay?.border_color);
	applyCssVar("kiosk-lt-primary", data.kiosk_auth_overlay?.lt_primary_color);
	applyCssVar("kiosk-muted-text", data.kiosk_auth_overlay?.muted_text_color);
	applyCssVar("kiosk-lt-additional-bg", data.kiosk_auth_overlay?.lt_background_addinational_color);
	applyCssVar("kiosk-drum-wrf-accent-color", data.kiosk_drum_wrf?.accent_color);
	applyCssVar("kiosk-drum-wrf-background-color", data.kiosk_drum_wrf?.background_color);

	const accentColorWeak = data.accent_color ? tinycolor(data.accent_color).setAlpha(0.3).toRgbString() : "rgba(0, 0, 0, 0.08)";
	applyCssVar("accentColorWeak", accentColorWeak);

	const accentColorLight50 = data.accent_color ? tinycolor.mix(data.accent_color, "#ffffff", 50).toHexString() : "rgba(255, 255, 255, 0.28)";
	applyCssVar("accentColorLight50", accentColorLight50);

	if (data.apply_custom_size_picker_fonts && data.size_picker_unselected_font_size && data.size_picker_unselected_font_size > 0)
		applyCssVar("sizePickerUnselectedFontSize", `${data.size_picker_unselected_font_size}rem`);
	if (data.apply_custom_size_picker_fonts && data.size_picker_selected_font_size && data.size_picker_selected_font_size > 0)
		applyCssVar("sizePickerSelectedFontSize", `${data.size_picker_selected_font_size}rem`);
};

interface MarkingConfirmationDesignFields {
	kiosk_marking_confirmation_barcode?: string | null;
	kiosk_marking_confirmation_pin?: string | null;
	kiosk_marking_confirmation_warning_text?: string | null;
}

interface LikesDesignFields {
	display_likes?: boolean | null;
}

export const useThemeStore = defineStore("themeLib", () => {
	const mainStore = useMainStore();
	const orgUnit = useOrgUnitStore();

	const organizationId = computed(() => orgUnit.data?.organization as string);

	const { data: response, refetch: refetchTheme } = useOrgOrganizationsDesignRetrieve(organizationId, {
		query: {
			placeholderData: keepPreviousData,
			enabled: computed(() => !!organizationId.value),
			gcTime: Infinity,
			staleTime: Infinity, //
			refetchOnReconnect: true,
			refetchOnWindowFocus: false,
			refetchOnMount: false,
			persister: persister.persisterFn,
		},
	});

	// Видео для превью панели: в превью оно заменяет прелоадер и перекрывает свои фоны экранов
	const previewPreloader = computed(() => (isPreviewMode && response.value ? previewPreloaderOf(response.value) : null));

	const data = computed(() => {
		const local = response.value as (NewDesignConfig & MarkingConfirmationDesignFields & LikesDesignFields) | null | undefined;
		if (!local) return local;

		const primaryColor = local.primary_color || "#F2F7FF";
		const rgbaColor = tinycolor(primaryColor).setAlpha(0.7).toRgbString();

		return {
			...local,
			kiosk_preloader: previewPreloader.value || local.kiosk_preloader,
			loader_screen: defu(local.loader_screen, { type: null, image_link: null, phrase_interval: null, phrase: { created: [], fiscalized: [], completed: [] } }),
			info_blocks: defu(local.info_blocks, { kiosk_loyalty: [], loyalty: [] }) as any, // @TODO: need backend fix (сейчас генерится что это строка а не объект)
			gradientStyle: {
				background: `linear-gradient(to bottom, transparent 10%, ${rgbaColor} 50%, ${primaryColor} 85%)`,
				backgroundColor: `-webkit-linear-gradient(to bottom, transparent 10%, ${rgbaColor} 50%, ${primaryColor} 85%)`,
			},
		};
	});

	/**
	 * Для сохранения старой логики чтобы не поломалось на мерджах с другими ветками
	 * После релиза удалить этот computed
	 * @deprecated теперь нужно использовать `data`
	 * @example
	 * ```ts
	 * themeStore.itemThemes.primary_color → themeStore.data.primary_color
	 * ```
	 */
	const itemThemes = computed(() => data.value);

	const refetch = (options?: RefetchOptions | undefined): Promise<QueryObserverResult<NewDesignConfig, ResponseErrorConfig<Error>>> => {
		return refetchTheme(options);
	};

	const onUpdate = (newData: NewDesignConfig | null | undefined, _oldData: NewDesignConfig | null | undefined) => {
		if (!newData) return;

		mainStore.setStateDrum(!!newData.hide_barban);
		mainStore.setKioskTimeout(newData.kiosk_timeout ?? 0);

		applyColorVars(newData);
		syncDesignFonts(newData as unknown as Record<string, unknown>);

		localStorage.setItem("kiosk_disable_lock_screen", String(newData.kiosk_disable_lock_screen));
	};

	watch(data, onUpdate, { immediate: true, deep: true });

	return {
		data,
		previewPreloader,
		refetch,
		itemThemes,
	};
});
